import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseHeadsetDetectionReturn {
  headsetConnected: boolean;
  usingFallbackSpeaker: boolean;
  audioOutputDevice: MediaDeviceInfo | null;
}

/** Keywords (lowercase) that indicate a Bluetooth audio device. */
const BLUETOOTH_KEYWORDS = ['bluetooth', 'bt', 'airpods', 'wireless', 'headphones bt'];

/** Keywords (lowercase) that indicate a wired headset/headphone. */
const WIRED_HEADSET_KEYWORDS = ['headphone', 'headset', 'earphone', 'earbuds'];

function labelMatchesKeywords(label: string, keywords: string[]): boolean {
  const lowerLabel = label.toLowerCase();
  return keywords.some((kw) => lowerLabel.includes(kw));
}

function isBluetoothDevice(device: MediaDeviceInfo): boolean {
  return labelMatchesKeywords(device.label, BLUETOOTH_KEYWORDS);
}

function isWiredHeadset(device: MediaDeviceInfo): boolean {
  return labelMatchesKeywords(device.label, WIRED_HEADSET_KEYWORDS);
}

function isHeadset(device: MediaDeviceInfo): boolean {
  return isBluetoothDevice(device) || isWiredHeadset(device);
}

/**
 * Enumerates audio output devices and returns the first headset found, or null.
 *
 * Note: `enumerateDevices()` returns labeled devices only when the user has
 * granted microphone permission (navigator.permissions). Without permission,
 * labels are empty strings — the hook handles this gracefully.
 */
async function findHeadsetOutputDevice(): Promise<MediaDeviceInfo | null> {
  const devices = await navigator.mediaDevices.enumerateDevices();
  const outputDevices = devices.filter((d) => d.kind === 'audiooutput');
  const headset = outputDevices.find(isHeadset);
  return headset ?? null;
}

/**
 * useHeadsetDetection — detects Bluetooth and wired headsets via enumerateDevices(),
 * monitors for mid-session device changes, and signals fallback speaker mode on
 * headset disconnect.
 *
 * When the headset disconnects mid-session the caller should display an amber
 * warning banner and may reduce the audio output volume to ~60% (this hook
 * exposes the state; the caller controls the volume).
 *
 * Cleans up the 'devicechange' listener on unmount.
 */
export function useHeadsetDetection(): UseHeadsetDetectionReturn {
  const [headsetConnected, setHeadsetConnected] = useState(false);
  const [usingFallbackSpeaker, setUsingFallbackSpeaker] = useState(false);
  const [audioOutputDevice, setAudioOutputDevice] = useState<MediaDeviceInfo | null>(null);

  // Track whether a headset was connected before the last devicechange event
  // so we can detect disconnect vs. connect transitions.
  const prevHeadsetRef = useRef<MediaDeviceInfo | null>(null);

  const checkDevices = useCallback(async () => {
    try {
      const headset = await findHeadsetOutputDevice();

      if (headset) {
        // Headset found or reconnected.
        setHeadsetConnected(true);
        setUsingFallbackSpeaker(false);
        setAudioOutputDevice(headset);
        prevHeadsetRef.current = headset;
      } else {
        // No headset detected.
        const wasConnected = prevHeadsetRef.current !== null;
        setHeadsetConnected(false);
        setAudioOutputDevice(null);
        prevHeadsetRef.current = null;

        if (wasConnected) {
          // Headset was previously connected — signal fallback speaker mode.
          setUsingFallbackSpeaker(true);
        }
      }
    } catch (err) {
      // enumerateDevices() can fail in insecure contexts or restricted browsers.
      console.warn('[useHeadsetDetection] enumerateDevices() failed:', err);
    }
  }, []);

  useEffect(() => {
    // Initial detection on mount.
    void checkDevices();

    // Monitor for mid-session device changes.
    const handleDeviceChange = () => {
      void checkDevices();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [checkDevices]);

  return { headsetConnected, usingFallbackSpeaker, audioOutputDevice };
}
